<template>
  <div class="commercial-home">
    <template v-for="section in sections">
      <section v-if="section.type === 'image_showcase'" :key="section.id" class="image-showcase" :style="showcaseStyle(section)">
        <div v-if="!section.content.image_url" class="image-showcase__empty"><v-icon size="72" color="grey lighten-1">mdi-image-outline</v-icon></div>
      </section>
      <section
        v-else-if="section.type === 'services'"
        :key="section.id"
        class="home-block benefits-block"
        :style="sectionStyle(section)"
      >
        <v-container>
          <header class="block-heading">
            <h2 :style="titleStyle(section)">{{ section.content.title || 'Por qué elegirnos' }}</h2>
            <p v-if="section.content.subtitle">{{ section.content.subtitle }}</p>
          </header>
          <v-row>
            <v-col v-for="(item, index) in section.content.items || []" :key="index" cols="12" sm="6" lg="3">
              <article class="benefit-card">
                <v-icon size="34" color="primary">{{ item.icon || 'mdi-check-decagram-outline' }}</v-icon>
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
              </article>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <section
        v-else-if="section.type === 'brands'"
        :key="section.id"
        class="home-block brands-block"
        :style="sectionStyle(section)"
      >
        <v-container>
          <header class="block-heading">
            <h2 :style="titleStyle(section)">{{ section.content.title }}</h2>
            <p v-if="section.content.subtitle">{{ section.content.subtitle }}</p>
          </header>
          <v-carousel
            v-if="configuredBrandItems(section).length"
            height="150"
            hide-delimiters
            show-arrows-on-hover
            :cycle="section.content.autoplay !== false"
            :interval="Number(section.content.interval_seconds || 3) * 1000"
            class="brand-slider"
          >
            <v-carousel-item v-for="(group, groupIndex) in brandGroups(section)" :key="groupIndex">
              <div class="brand-row">
                <a v-for="(brand, index) in group" :key="index" :href="brand.url || undefined" :target="brand.url ? '_blank' : undefined" rel="noopener" class="brand-card">
                  <v-img :src="brand.image_url" :alt="brand.alt || brand.name || ''" contain class="brand-logo" :class="{ grayscale: section.styles.grayscale }" />
                  <span v-if="brand.name">{{ brand.name }}</span>
                </a>
              </div>
            </v-carousel-item>
          </v-carousel>
          <div v-else class="brand-empty" aria-label="Espacio reservado para logos de marcas">
            <span v-for="index in 5" :key="index" class="brand-empty__item"><v-icon color="grey lighten-1" size="34">mdi-image-outline</v-icon></span>
          </div>
        </v-container>
      </section>

      <section
        v-else-if="section.type === 'categories'"
        :key="section.id"
        class="home-block categories-block"
        :style="sectionStyle(section)"
      >
        <v-container>
          <header class="block-heading"><h2 :style="titleStyle(section)">{{ section.content.title || 'Categorías' }}</h2><p v-if="section.content.subtitle">{{ section.content.subtitle }}</p></header>
          <div class="category-grid">
            <router-link v-for="category in categories.slice(0, Number(section.content.limit || 6))" :key="category.id" :to="`/productos?categoria=${category.id}`" class="category-card">
              <div class="category-visual"><v-icon size="44" color="primary">mdi-shape-outline</v-icon></div><strong>{{ category.nombre }}</strong><span>Ver productos <v-icon small>mdi-arrow-right</v-icon></span>
            </router-link>
          </div>
        </v-container>
      </section>

      <section
        v-else-if="section.type === 'featured_products'"
        :key="section.id"
        class="home-block products-block"
        :style="sectionStyle(section)"
      >
        <v-container>
          <header class="block-heading">
            <h2 :style="titleStyle(section)">{{ section.content.title }}</h2>
            <p v-if="section.content.subtitle">{{ section.content.subtitle }}</p>
          </header>
          <v-row>
            <v-col v-for="product in productsFor(section)" :key="product.id" cols="12" sm="6" lg="3">
              <v-card class="product-card fill-height" :to="`/productos/${product.id}`" elevation="0">
                <v-img :src="productImage(product)" height="230" cover class="product-image" />
                <v-card-text>
                  <div class="product-category">{{ product.categoria ? product.categoria.nombre : '' }}</div>
                  <h3>{{ product.nombre || product.descripcion }}</h3>
                  <div class="product-price">{{ money(product.precio) }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <div class="block-action">
            <v-btn color="primary" large :to="section.content.button_url || '/productos'">
              {{ section.content.button_text || 'Ver productos' }}
            </v-btn>
          </div>
        </v-container>
      </section>

      <section
        v-else-if="section.type === 'promo_banner'"
        :key="section.id"
        class="promo-wrap"
      >
        <v-container>
          <div class="promo-banner" :style="promoStyle(section)">
            <div class="promo-overlay" :style="promoOverlay(section)"></div>
            <div class="promo-content" :class="`text-${section.styles.alignment || 'left'}`">
              <span v-if="section.content.eyebrow" class="promo-eyebrow">{{ section.content.eyebrow }}</span>
              <h2 :style="titleStyle(section)">{{ section.content.title }}</h2>
              <p>{{ section.content.subtitle }}</p>
              <v-btn v-if="section.content.button_text" color="primary" large :to="section.content.button_url || '/productos'">
                {{ section.content.button_text }}
              </v-btn>
            </div>
          </div>
        </v-container>
      </section>

      <section
        v-else-if="section.type === 'image_text'"
        :key="section.id"
        class="home-block image-text-block"
        :style="sectionStyle(section)"
      >
        <v-container><div class="image-text" :class="{ reverse: section.styles.image_position === 'right' }">
          <div class="image-text__visual" :style="sectionImage(section)"><v-icon v-if="!section.content.image_url" size="64" color="grey lighten-1">mdi-image-outline</v-icon></div>
          <div class="image-text__copy"><span v-if="section.content.eyebrow" class="section-eyebrow">{{ section.content.eyebrow }}</span><h2 :style="titleStyle(section)">{{ section.content.title }}</h2><p>{{ section.content.subtitle }}</p><v-btn v-if="section.content.button_text" color="primary" large :to="section.content.button_url || '/nosotros'">{{ section.content.button_text }}</v-btn></div>
        </div></v-container>
      </section>

      <section
        v-else-if="section.type === 'store_info'"
        :key="section.id"
        class="home-block store-info-block"
        :style="sectionStyle(section)"
      >
        <v-container><header class="block-heading"><h2 :style="titleStyle(section)">{{ section.content.title || 'Visitá nuestro local' }}</h2></header><v-row>
          <v-col v-if="site.contact && site.contact.address" cols="12" md="3"><div class="info-card"><v-icon color="primary">mdi-map-marker-outline</v-icon><strong>Dirección</strong><span>{{ site.contact.address }}</span></div></v-col>
          <v-col v-if="site.contact && site.contact.phone" cols="12" md="3"><div class="info-card"><v-icon color="primary">mdi-phone-outline</v-icon><strong>Teléfono</strong><span>{{ site.contact.phone }}</span></div></v-col>
          <v-col v-if="section.content.hours" cols="12" md="3"><div class="info-card"><v-icon color="primary">mdi-clock-outline</v-icon><strong>Horarios</strong><span>{{ section.content.hours }}</span></div></v-col>
          <v-col v-if="site.contact && site.contact.whatsapp" cols="12" md="3"><div class="info-card"><v-icon color="primary">mdi-whatsapp</v-icon><strong>WhatsApp</strong><span>{{ site.contact.whatsapp }}</span></div></v-col>
        </v-row></v-container>
      </section>

      <section
        v-else-if="section.type === 'footer_cta'"
        :key="section.id"
        class="home-block cta-block"
        :style="sectionStyle(section)"
      >
        <v-container class="cta-inner">
          <div>
            <h2 :style="titleStyle(section)">{{ section.content.title }}</h2>
            <p>{{ section.content.subtitle }}</p>
          </div>
          <v-btn x-large color="white" light :to="section.content.button_url || '/contacto'">
            {{ section.content.button_text || 'Contactanos' }}
          </v-btn>
        </v-container>
      </section>
    </template>
  </div>
</template>

<script>
import ProductoService from '@/service/ProductoService';
import CategoriaService from '@/service/CategoriaService';
import { siteState } from '@/site/siteConfig';

export default {
  name: 'CommercialHomeSections',
  props: {
    sections: { type: Array, default: () => [] },
  },
  data: () => ({ products: [], categories: [] }),
  computed: { site() { return siteState.data.site || {}; } },
  mounted() {
    ProductoService.listar().then(({ data }) => { this.products = Array.isArray(data) ? data : []; }).catch(() => {});
    CategoriaService.listar().then(({ data }) => { this.categories = Array.isArray(data) ? data : []; }).catch(() => {});
  },
  methods: {
    sectionStyle(section) {
      return {
        backgroundColor: section.styles.background_color || '#fff',
        color: section.styles.text_color || 'var(--site-text)',
      };
    },
    titleStyle(section) { return { color: section.styles.title_color || 'var(--site-text)' }; },
    configuredBrandItems(section) {
      return (section.content.items || []).filter(item => item.image_url);
    },
    brandGroups(section) {
      const perPage = Number(this.$vuetify.breakpoint.smAndDown ? section.styles.items_mobile || 2 : section.styles.items_desktop || 5);
      const items = this.configuredBrandItems(section);
      const groups = [];
      for (let index = 0; index < items.length; index += perPage) groups.push(items.slice(index, index + perPage));
      return groups;
    },
    productsFor(section) {
      const ids = Array.isArray(section.content.product_ids) ? section.content.product_ids.map(Number) : [];
      const selected = section.content.mode === 'manual' && ids.length
        ? ids.map(id => this.products.find(product => Number(product.id) === id)).filter(Boolean)
        : this.products;
      return selected.slice(0, Number(section.content.limit || 8));
    },
    productImage(product) {
      const cover = product && product.portada;
      if (cover && cover.url) return cover.url;
      if (cover && cover.contenido) {
        if (cover.contenido.startsWith('data:')) return cover.contenido;
        return `data:${cover.mimetype || 'image/jpeg'};base64,${cover.contenido}`;
      }
      return null;
    },
    money(value) {
      return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value || 0));
    },
    promoStyle(section) {
      const mobile = this.$vuetify.breakpoint.smAndDown && section.content.background_mobile_url;
      const image = mobile || section.content.background_desktop_url;
      return {
        backgroundColor: section.styles.background_color || '#111827',
        backgroundImage: image ? `url(${image})` : undefined,
        minHeight: `${Number(this.$vuetify.breakpoint.smAndDown ? section.styles.min_height_mobile || 360 : section.styles.min_height_desktop || 380)}px`,
      };
    },
    promoOverlay(section) {
      return { background: `rgba(0,0,0,${Number(section.styles.overlay_opacity || 0) / 100})` };
    },
    sectionImage(section) { return section.content.image_url ? { backgroundImage: `url(${section.content.image_url})` } : {}; },
    showcaseStyle(section) { return { minHeight: `${Number(this.$vuetify.breakpoint.smAndDown ? section.styles.height_mobile || 70 : section.styles.height_desktop || 100)}vh`, backgroundImage: section.content.image_url ? `url(${section.content.image_url})` : undefined }; },
  },
};
</script>

<style scoped>
.home-block { padding: 88px 0; }
.image-showcase{width:100%;background:#e5e7eb center/cover no-repeat}.image-showcase__empty{min-height:inherit;display:grid;place-items:center}
.block-heading { max-width: 720px; margin: 0 auto 42px; text-align: center; }
.block-heading h2, .promo-content h2, .cta-inner h2 { font-family: var(--site-heading-font); font-size: clamp(2rem, 4vw, 3.25rem); line-height: 1.1; margin-bottom: 14px; }
.block-heading p, .promo-content p, .cta-inner p { font-size: 1.08rem; line-height: 1.7; margin: 0; }
.benefit-card { height: 100%; padding: 28px; border: 1px solid rgba(15,23,42,.08); border-radius: var(--site-radius); background: #fff; box-shadow: 0 14px 40px rgba(15,23,42,.06); }
.benefit-card h3 { margin: 18px 0 8px; font-size: 1.12rem; }
.benefit-card p { margin: 0; line-height: 1.6; }
.brand-card { width: 190px; min-height: 128px; margin: 8px; padding: 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: inherit; text-decoration: none; border: 1px solid rgba(15,23,42,.08); border-radius: var(--site-radius); background: #fff; }
.brand-row { height: 100%; display: flex; align-items: center; justify-content: center; }
.brand-empty { min-height: 150px; display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 16px; padding: 10px 0; }
.brand-empty__item { display: flex; align-items: center; justify-content: center; min-height: 120px; border: 1px dashed #d1d5db; border-radius: var(--site-radius); background: #eef0f3; }
.brand-logo { width: 140px; height: 72px; transition: filter .25s, transform .25s; }
.brand-logo.grayscale { filter: grayscale(1); opacity: .72; }
.brand-card:hover .brand-logo { filter: none; opacity: 1; transform: scale(1.04); }
.product-card { overflow: hidden; border: 1px solid rgba(15,23,42,.08); border-radius: var(--site-radius) !important; transition: transform .2s, box-shadow .2s; }
.category-grid { display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px }.category-card{padding:22px;color:#0f172a;text-decoration:none;border:1px solid #e2e8f0;border-radius:var(--site-radius);background:#fff;transition:.2s}.category-card:hover{transform:translateY(-4px);box-shadow:0 16px 36px rgba(15,23,42,.1)}.category-visual{height:110px;display:grid;place-items:center;margin-bottom:16px;border-radius:calc(var(--site-radius) - 4px);background:#f1f5f9}.category-card strong,.category-card span{display:block}.category-card span{margin-top:7px;color:#64748b;font-size:.85rem}
.image-text{display:grid;grid-template-columns:1fr 1fr;align-items:center;gap:64px}.image-text.reverse .image-text__visual{order:2}.image-text__visual{min-height:440px;display:grid;place-items:center;border-radius:calc(var(--site-radius) + 8px);background:#e5e7eb center/cover no-repeat}.image-text__copy h2{font-size:clamp(2rem,4vw,3.2rem);line-height:1.1;margin:10px 0 18px}.image-text__copy p{font-size:1.08rem;line-height:1.8;margin-bottom:26px}.section-eyebrow{color:var(--site-primary);font-weight:800;letter-spacing:.12em}.info-card{height:100%;display:flex;flex-direction:column;gap:10px;padding:28px;border:1px solid #e2e8f0;border-radius:var(--site-radius);background:#fff}.info-card .v-icon{align-self:flex-start}.info-card strong{font-size:1.1rem}.info-card span{color:#64748b;line-height:1.6}
.product-card:hover { transform: translateY(-5px); box-shadow: 0 18px 45px rgba(15,23,42,.12) !important; }
.product-card h3 { color: #111827; font-size: 1.08rem; margin: 5px 0 12px; }
.product-category { color: #64748b; font-size: .75rem; min-height: 18px; text-transform: uppercase; letter-spacing: .08em; }
.product-price { color: var(--site-primary); font-size: 1.25rem; font-weight: 800; }
.block-action { margin-top: 38px; text-align: center; }
.promo-wrap { padding: 64px 0; }
.promo-banner { position: relative; display: flex; align-items: center; overflow: hidden; padding: 56px; border-radius: calc(var(--site-radius) + 8px); background-size: cover; background-position: center; }
.promo-overlay { position: absolute; inset: 0; }
.promo-content { position: relative; width: min(100%, 650px); color: white; }
.promo-eyebrow { display: inline-block; margin-bottom: 14px; font-weight: 800; letter-spacing: .12em; }
.promo-content p { margin-bottom: 26px; }
.cta-block { padding: 64px 0; }
.cta-inner { display: flex; align-items: center; justify-content: space-between; gap: 32px; }
.cta-inner h2 { margin-bottom: 8px; }
@media (max-width: 600px) {
  .home-block { padding: 60px 0; }
  .promo-banner { padding: 34px 24px; }
  .cta-inner { flex-direction: column; align-items: flex-start; }
  .brand-empty { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .brand-empty__item:nth-child(n+3) { display: none; }
  .category-grid { grid-template-columns:1fr 1fr; }
  .image-text { grid-template-columns:1fr;gap:30px }.image-text.reverse .image-text__visual{order:0}.image-text__visual{min-height:300px}
}
</style>
